import { useEffect, useState } from "react";
import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { RemoteCompany } from "../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { CompanyUnits } from "../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { Colleague } from "../../../features/Connections/types/Colleague.type";
import { initialItemPerm } from "./List-Permission-Select.function";

type P = {
  permRow: {
    affectedCompanies: string[];
    affectedDepartments: string[];
    affectedUsers: string[];
  };
  remoteCompanies: RemoteCompany[] | undefined;
  remoteDepartments: CompanyUnits[] | undefined;
  remoteAllUsers: Colleague[] | undefined;
};

export interface ItemPermission {
  rowId: number;
  affectedCompanies: string[];
  affectedDepartments: string[];
  affectedUsers: string[];
}

export interface ItemPermEntityOptions {
  id: string;
  name: string;
}

export enum ItemPermEntityType {
  COMPANY = "Company",
  DEPARTMENT = "Department",
  USER = "User"
}

export type ItemPermissionOption = {
  id: string;
  name: string;
};

export function ItemPermissionSelect({
  permRow,
  remoteCompanies,
  remoteDepartments,
  remoteAllUsers
}: Readonly<P>) {
  const { t } = useTranslation();

  const [selectedCompanies, setSelectedCompanies] = useState<ItemPermissionOption[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<ItemPermissionOption[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<ItemPermissionOption[]>([]);

  const companyOptions: ItemPermissionOption[] =
    remoteCompanies?.map(comp => ({
      id: comp.id.toString(),
      name: comp.name ?? `${t("Company")} ${comp.id}`
    })) ?? [];
  const departmentOptions: ItemPermissionOption[] =
    remoteDepartments?.map(depart => ({
      id: depart.id.toString(),
      name: depart.name ?? `${t("Department")} ${depart.id}`
    })) ?? [];
  const userOptions: ItemPermissionOption[] =
    remoteAllUsers?.map(user => ({
      id: user.userId,
      name: `${user.firstName} ${user.surname}`
    })) ?? [];

  const handleCompanyChange = (event: any, value: ItemPermissionOption[]) => {
    setSelectedCompanies(value);
    updatePerms(value, ItemPermEntityType.COMPANY);
  };
  const handleDepartmentChange = (event: any, value: ItemPermissionOption[]) => {
    setSelectedDepartments(value);
    updatePerms(value, ItemPermEntityType.DEPARTMENT);
  };
  const handleUserChange = (event: any, value: ItemPermissionOption[]) => {
    setSelectedUsers(value);
    updatePerms(value, ItemPermEntityType.USER);
  };

  const updatePerms = (value: ItemPermissionOption[], entity: ItemPermEntityType) => {
    switch (entity) {
      case ItemPermEntityType.COMPANY: {
        permRow.affectedCompanies = value.map(val => val.id);
        break;
      }
      case ItemPermEntityType.DEPARTMENT: {
        permRow.affectedDepartments = value.map(val => val.id);
        break;
      }
      case ItemPermEntityType.USER: {
        permRow.affectedUsers = value.map(val => val.id);
        break;
      }
    }
  };

  useEffect(() => {
    // without relevant data, cannot apply values
    if (!remoteCompanies || !remoteDepartments || !remoteAllUsers) return;

    /** initially update permission values */
    initialItemPerm({
      props: { permRow, remoteCompanies, remoteDepartments, remoteAllUsers },
      actions: {
        setSelectedCompanies,
        setSelectedDepartments,
        setSelectedUsers,
        t
      }
    });
  }, []);

  return (
    <Box>
      {/* permission of companies */}
      <Autocomplete
        id="items-autocomplete"
        options={companyOptions}
        sx={{ my: 3 }}
        multiple
        disableCloseOnSelect
        autoComplete
        value={selectedCompanies}
        onChange={(e, value) => handleCompanyChange(e, value)}
        limitTags={4}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(opt, value) => opt.id === value.id}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              data-testid={`${option.id}-select-chip`}
              label={option.name}
            />
          ));
        }}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            placeholder={t("Company")}
            data-testid="catering-Company-perm-autocomplete"
          />
        )}
      />

      {/* permission of departments */}
      <Autocomplete
        sx={{ my: 3 }}
        multiple
        limitTags={4}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(opt, value) => opt.id === value.id}
        id="items-autocomplete"
        disableCloseOnSelect
        autoComplete
        options={departmentOptions}
        value={selectedDepartments}
        onChange={(e, value) => handleDepartmentChange(e, value)}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            placeholder={t("Department")}
            data-testid="catering-Department-perm-autocomplete"
          />
        )}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              data-testid={`${option.id}-select-chip`}
              label={option.name}
            />
          ));
        }}
      />

      {/* permission of companies */}
      <Autocomplete
        multiple
        autoComplete
        disableCloseOnSelect
        sx={{ my: 3 }}
        limitTags={4}
        id="items-autocomplete"
        options={userOptions}
        value={selectedUsers}
        onChange={(e, value) => handleUserChange(e, value)}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(opt, value) => opt.id === value.id}
        renderTags={(tagValue, getTagProps) => {
          return tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={option.name}
              data-testid={`${option.id}-select-chip`}
            />
          ));
        }}
        renderInput={params => (
          <TextField
            {...params}
            fullWidth
            data-testid="catering-User-perm-autocomplete"
            placeholder={t("User")}
          />
        )}
      />
    </Box>
  );
}
